import { getApiBase } from "@/lib/apiBase";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

const TOKEN_KEY = "mr_token";
const FALLBACK_GUEST = ["steam", "humble", "cook", "chat", "syncwatch"];

export type AuthUser = {
  username: string;
  role?: string | null;
  email?: string | null;
  permissions?: string[];
  effective?: string[];
  disabled?: boolean;
};

const ROLE_MAP: Record<string, string[]> = {
  admin: ["steam", "humble", "cook", "chat", "ytdl", "ai", "users", "steam_archive", "syncwatch"],
  editor: [],
  viewer: [],
};

export const useAuthStore = defineStore("auth", () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || "");
  const user = ref<AuthUser | null>(null);
  const guestPerms = ref<string[]>([]);
  const guestLoaded = ref(false);
  const error = ref("");
  const loading = ref(false);

  const isLoggedIn = computed(() => Boolean(token.value && user.value));
  const isAdmin = computed(() => user.value?.role === "admin");

  function can(perm: string) {
    if (!user.value) return guestPerms.value.includes(perm);
    if (user.value.role === "admin") return true;
    if (user.value.effective?.includes(perm)) return true;
    const role = user.value.role || "viewer";
    return (ROLE_MAP[role] || []).includes(perm) || (user.value.permissions || []).includes(perm);
  }

  function setToken(value: string) {
    token.value = value;
    if (value) localStorage.setItem(TOKEN_KEY, value);
    else localStorage.removeItem(TOKEN_KEY);
  }

  async function fetchMe() {
    if (!token.value) {
      user.value = null;
      return;
    }
    try {
      const response = await fetch(`${getApiBase()}/auth/users/me`, {
        headers: { Authorization: `Bearer ${token.value}` },
      });
      if (!response.ok) {
        setToken("");
        user.value = null;
        return;
      }
      user.value = await response.json();
    } catch {
      user.value = null;
    }
  }

  async function login(username: string, password: string) {
    loading.value = true;
    error.value = "";
    try {
      const body = new URLSearchParams({ username, password });
      const response = await fetch(`${getApiBase()}/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || "Login fehlgeschlagen");
      }
      setToken(data.access_token);
      await fetchMe();
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Login fehlgeschlagen";
      setToken("");
      user.value = null;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function logout() {
    setToken("");
    user.value = null;
  }

  async function fetchGuest() {
    try {
      const response = await fetch(`${getApiBase()}/auth/guest`);
      const data = await response.json().catch(() => ({}));
      if (response.ok && Array.isArray(data.permissions)) {
        guestPerms.value = data.permissions;
      } else {
        guestPerms.value = FALLBACK_GUEST;
      }
    } catch {
      guestPerms.value = FALLBACK_GUEST;
    } finally {
      guestLoaded.value = true;
    }
  }

  return {
    token,
    user,
    guestPerms,
    guestLoaded,
    error,
    loading,
    isLoggedIn,
    isAdmin,
    can,
    login,
    logout,
    fetchMe,
    fetchGuest,
  };
});
